
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import {
  ButtonComponent,
  DialogService,
  EmptyComponent,
  FileUploadComponent,
  InputComponent,
  SelectComponent,
  type UploadedFile,
} from 'nebula-ui-kit';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderAction,
  PageHeaderComponent,
} from '@nebula-shared/ui';
import {
  catchError,
  concatMap,
  finalize,
  forkJoin,
  from,
  map,
  of,
  switchMap,
  takeWhile,
  timeout,
  toArray,
} from 'rxjs';
import { IngestProjectService } from '../../../../../data-access/src/lib/ingest/ingest-project.service';
import {
  formatIngestDuplicateConfirmMessage,
  ingestDuplicateCheckFromUploadError,
  ingestDuplicateCheckRequiresConfirmation,
  isIngestDuplicateRequiresConfirmationError,
  resolveIngestDuplicateCheckTargets,
} from '../../../../../data-access/src/lib/ingest/ingest-duplicate.util';
import { ExpedienteUploadService } from '../../../../../data-access/src/lib/ingest/expediente-upload.service';
import {
  expedienteBaseFromIso,
  groupByRole,
  isExpedienteSelection,
} from '../../../../../data-access/src/lib/ingest/expediente-detect';
import {
  IngestProject,
  IngestSubfolder,
  IngestUploadResponse,
  IngestDuplicateCheckResponse,
} from '../../../../../domain/src/lib/ingest/ingest-project.model';
import { INGEST_MODULE_ENV } from '@nebula/shared-vault/data-access';
import { getMockSubfolders, resolvePreviewProject } from '../ingest-ui-mock.data';
import {
  UploadProgressLine,
  UploadProgressTrackerComponent,
} from '../upload-progress-tracker/upload-progress-tracker.component';
import { ingestUiStore } from '../../../../../store/src/lib/ingest-ui.store';
import { shouldResetIngestUploadMetaAfterBatch } from '../ingest-upload-batch.util';
import {
  ingestInactiveProjectMessage,
  isIngestProjectEligibleForUpload,
} from '../ingest-project-active.util';
import { openIngestAlertDialog, openIngestConfirmDialog } from '../ingest-dialog.util';
import {
  canLeaveIngestUploadScreen,
  INGEST_UPLOAD_IN_PROGRESS_LEAVE_MESSAGE,
  INGEST_UPLOAD_IN_PROGRESS_LEAVE_TITLE,
  resolveIngestDuplicateConfirmDecision,
} from '../ingest-upload-leave.util';
import { messageFromIngestHttpError } from '../ingest-http-error.util';
import {
  INGEST_UPLOAD_ACCEPT_EXTENSIONS,
  INGEST_UPLOAD_HINT_TYPES,
  INGEST_UPLOAD_MAX_FILE_BYTES,
  formatIngestUploadFolderRejectionAlert,
  formatIngestUploadMaxSizeLabel,
  getIngestUploadFileValidationIssue,
  type IngestUploadFileValidationIssue,
  validateIngestUploadFile,
} from '../ingest-upload-file.util';
import {
  isIngestDirectoryPickerAvailable,
  pickIngestFolderFiles,
  type IngestFolderFileEntry,
} from '../ingest-folder-picker.util';
import {
  formatIngestSubfolderDisplay,
  ingestSubfolderPathFromSelectValue,
  normalizeIngestSubfolders,
  resolveIngestSubfolderSelectValue,
} from '../ingest-subfolder.util';
import {
  appendIngestUploadMetaFields,
  snapshotIngestUploadMeta,
  type IngestUploadMetaSnapshot,
} from '../ingest-upload-meta.util';

/**
 * Construye el mensaje para el usuario a partir de la respuesta HTTP.
 * Evita el texto genérico "Http failure response for …" y prioriza `detail` / cuerpo JSON del API.
 */
function messageFromIngestUploadError(err: unknown): string {
  if (isIngestDuplicateRequiresConfirmationError(err)) {
    return 'Esta carga requiere confirmación para crear una nueva versión del documento.';
  }
  if (err instanceof HttpErrorResponse) {
    const b = err.error;
    if (b && typeof b === 'object' && !Array.isArray(b)) {
      const o = b as Record<string, unknown>;
      if (typeof o['detail'] === 'string' && o['detail'].trim()) {
        return o['detail'].trim();
      }
      if (typeof o['message'] === 'string' && o['message'].trim()) {
        return o['message'].trim();
      }
    }
    if (typeof b === 'string' && b.trim()) {
      const t = b.trim();
      if (t.startsWith('{')) {
        try {
          const j = JSON.parse(t) as { detail?: string; message?: string };
          if (j.detail?.trim()) {
            return j.detail.trim();
          }
          if (j.message?.trim()) {
            return j.message.trim();
          }
        } catch {
          /* cuerpo no JSON */
        }
      }
      if (t.length < 500 && !t.toLowerCase().includes('<!doctype')) {
        return t;
      }
    }
    if (err.status === 500) {
      return 'El servidor devolvió un error interno (500) al procesar la subida. Es un fallo en el servicio de ingesta: hace falta revisar los logs del backend. Si aplica, confirma el contexto de tenant (X-Tenant-ID) y los datos del formulario.';
    }
    if (err.status === 0) {
      return 'No se pudo contactar al servidor. Revisa la red, el proxy y CORS.';
    }
    if (err.status === 401 || err.status === 403) {
      return 'No autorizado. Revisa la sesión o el token.';
    }
    if (err.status === 413) {
      return `El archivo supera el tamaño máximo permitido (${formatIngestUploadMaxSizeLabel(INGEST_UPLOAD_MAX_FILE_BYTES)}).`;
    }
    if (err.status === 400) {
      return 'Uno o más archivos no cumplen el formato o tamaño permitido.';
    }
    if (err.status) {
      return `Error ${err.status}${err.statusText ? ` (${err.statusText})` : ''} al subir.`;
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Error al subir el archivo. Revisa la red o el token.';
}

/**
 * Ruta relativa alineada con el backend (Linux): barras `/`, nunca `\`.
 * `webkitRelativePath` en Windows suele traer `\`; si el API une con `os.path` o strings Unix, el fichero no se encuentra.
 */
function normalizeIngestRelativePath(raw: string): string {
  return raw
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/+/g, '/')
    .replace(/^\/+/, '');
}

/** Elemento del listado de envío: archivos sueltos o archivos dentro de una o más carpetas (webkitRelativePath). */
interface IngestPendingEntry {
  file: File;
  relativePath: string;
  source: 'loose' | 'folder';
}

/** Diálogo modal al finalizar un envío (sustituye toasts: siempre con Aceptar / Cerrar). */
interface UploadResultDialogState {
  kind: 'success' | 'error';
  /** Texto de error o aviso sin detalle estructurado. */
  headline: string;
  /** Línea resumen (p. ej. N archivos). */
  intro?: string;
  /** Respuesta del backend del último archivo (o agregada) para mostrar estado, id, etc. */
  summary?: IngestUploadResponse | null;
}

@Component({
  selector: 'app-ingest-upload',
  standalone: true,
  imports: [
    ButtonComponent,
    EmptyComponent,
    PageHeaderComponent,
    FieldLabelComponent,
    GridFormComponent,
    GridFormItemComponent,
    InputComponent,
    SelectComponent,
    FileUploadComponent,
    UploadProgressTrackerComponent,
    MatButtonToggleModule
],
  templateUrl: './ingest-upload.component.html',
  styleUrls: ['./ingest-upload.component.scss'],
})
export class IngestUploadComponent implements OnInit {
  private readonly ingestProjectService = inject(IngestProjectService);
  private readonly expedienteUpload = inject(ExpedienteUploadService);
  private readonly ingestEnv = inject(INGEST_MODULE_ENV);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly uiStore = inject(ingestUiStore);
  private readonly dialogService = inject(DialogService);

  /**
   * Tamaño máximo por archivo (validación en `lib-file-upload` y carpeta; #0000039).
   */
  readonly maxFileBytes = INGEST_UPLOAD_MAX_FILE_BYTES;

  get maxFileSizeLabel(): string {
    return formatIngestUploadMaxSizeLabel(this.maxFileBytes);
  }

  /** Filtro del selector de archivos sueltos (HTML `accept`). */
  readonly acceptExtensions = INGEST_UPLOAD_ACCEPT_EXTENSIONS;

  readonly looseFilesHintTypes = INGEST_UPLOAD_HINT_TYPES;

  get folderFilesHint(): string {
    return `Tipos: ${INGEST_UPLOAD_HINT_TYPES}. Máx. ${this.maxFileSizeLabel} por archivo.`;
  }

  projectId: number | null = null;
  project: IngestProject | null = null;
  subfolders: IngestSubfolder[] = [];

  /** Signal para forzar actualización de vista si el flujo HTTP sale del zone. */
  readonly loading = signal(true);
  error: string | null = null;
  readonly subfoldersWarning = signal<string | null>(null);

  companyName = '';
  subfolderPath = '';
  description = '';

  /** Archivos elegidos con el selector estándar (se reemplaza en cada selección). */
  private looseFiles: File[] = [];
  /** Archivos añadidos desde una o varias carpetas (se acumulan). */
  private folderEntries: IngestPendingEntry[] = [];

  readonly filePickerReset = signal(0);
  /** Remonta metadatos tras carga OK y evita validación fantasma de `lib-input` (#0000034). */
  readonly metaFormResetKey = signal(0);

  /** Listado unificado (archivos sueltos + carpetas) para validación y envío. */
  private pendingEntries: IngestPendingEntry[] = [];

  uploadLines = signal<UploadProgressLine[]>([]);

  /**
   * Modo de carga detectado automáticamente desde la selección (solo lectura;
   * lo fija rebuildPendingQueue). 'expediente' = 1 .iso + 5 sidecars; si no, 'documento'.
   */
  readonly uploadMode = signal<'documento' | 'expediente'>('documento');

  /** Signal: el header (OnPush) debe reaccionar al cancelar el diálogo de versión. */
  readonly uploading = signal(false);
  lastUploadSummary: IngestUploadResponse | null = null;

  /** Ventana emergente (modal) al terminar un envío; el usuario cierra con Aceptar o Cerrar. */
  readonly uploadResultDialog = signal<UploadResultDialogState | null>(null);

  subfolderOptions = signal<{ label: string; value: string }[]>([]);

  readonly headerActions = computed<PageHeaderAction[]>(() => {
    const uploading = this.uploading();
    return [
      {
        id: 'back',
        // Habilitado para poder mostrar el aviso (disabled no dispara click en algunos kits).
        label: uploading ? 'Volver (carga en curso)' : 'Volver',
        icon: 'ArrowLeft',
        variant: 'secondary',
      },
      {
        id: 'upload',
        label: this.uploadMode() === 'expediente' ? 'Subir expediente' : 'Subir lote',
        icon: 'Upload',
        variant: 'primary',
        loading: uploading,
        disabled: this.uploadLines().length === 0 || uploading,
      },
    ];
  });

  get looseCount(): number {
    return this.looseFiles.length;
  }

  get folderQueueCount(): number {
    return this.folderEntries.length;
  }

  get totalQueued(): number {
    return this.pendingEntries.length;
  }

  /** Valor del `lib-select` (raíz usa sentinel; ver #0000031). */
  get subfolderSelectValue(): string {
    return resolveIngestSubfolderSelectValue(this.subfolderPath);
  }

  private snapshotUploadMetaForSend(): IngestUploadMetaSnapshot {
    return snapshotIngestUploadMeta({
      companyName: this.companyName,
      subfolderPath: this.subfolderPath,
      description: this.description,
    });
  }

  private openUploadResultDialog(state: UploadResultDialogState): void {
    this.uploadResultDialog.set({
      ...state,
      headline:
        state.headline?.trim() ||
        (state.kind === 'success' ? 'Operación completada.' : 'Se produjo un error.'),
    });
  }

  closeUploadResultDialog(): void {
    this.uploadResultDialog.set(null);
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((params) => Number(params.get('id'))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((id) => {
        if (!Number.isFinite(id) || id < 1) {
          this.loading.set(false);
          this.error = 'Identificador de expediente no válido.';
          return;
        }

        this.projectId = id;
        this.restoreUploadDraft(id);
        this.loadProjectAndSubfolders(id);
      });
  }

  private restoreUploadDraft(projectId: number): void {
    const d = this.uiStore.uploadDraftByProject()[String(projectId)];
    if (!d) return;
    this.companyName = d.companyName;
    this.subfolderPath = d.subfolderPath;
    this.description = d.description;
  }

  private persistUploadDraft(): void {
    if (!this.projectId) return;
    this.uiStore.setUploadDraftForProject(this.projectId, {
      companyName: this.companyName,
      subfolderPath: this.subfolderPath,
      description: this.description,
    });
  }

  /** Limpia metadatos de ingesta tras un envío exitoso (#0000034). */
  private resetUploadMetaForm(): void {
    this.companyName = '';
    this.subfolderPath = '';
    this.description = '';
    if (this.projectId) {
      this.uiStore.clearUploadDraftForProject(this.projectId);
    }
    this.metaFormResetKey.update((n) => n + 1);
  }

  private finalizeSuccessfulUpload(): void {
    this.resetUploadMetaForm();
    this.clearAllQueued();
  }

  onCompanyNameChange(value: string): void {
    this.companyName = value ?? '';
    this.persistUploadDraft();
  }

  onSubfolderPathChange(value: unknown): void {
    this.subfolderPath = ingestSubfolderPathFromSelectValue(
      value == null ? '' : String(value),
    );
    this.persistUploadDraft();
  }

  onDescriptionChange(value: string): void {
    this.description = value ?? '';
    this.persistUploadDraft();
  }

  private syncSubfolderOptions(): void {
    const opts = (this.subfolders ?? []).map((s) => ({
      label: formatIngestSubfolderDisplay(s),
      value: resolveIngestSubfolderSelectValue(s.path),
    }));
    this.subfolderOptions.set(opts);
  }

  private loadProjectAndSubfolders(id: number): void {
    this.loading.set(true);
    this.error = null;
    this.subfoldersWarning.set(null);
    this.project = null;
    this.subfolders = [];

    if (this.ingestEnv.previewMode) {
      const mock = resolvePreviewProject(id);
      if (!mock) {
        this.loading.set(false);
        this.error = 'Expediente no encontrado (vista prevía).';
        return;
      }
      this.project = mock;
      if (!this.applyInactiveProjectGuard(mock)) {
        this.subfolders = [];
        this.loading.set(false);
        return;
      }
      this.subfolders = getMockSubfolders(id);
      this.syncSubfolderOptions();
      this.loading.set(false);
      return;
    }

    /** Listado + subcarpetas; finalize asegura salir del loading aunque falle el subscribe. */
    this.ingestProjectService
      .getById(id)
      .pipe(
        timeout(45000),
        switchMap((project) =>
          this.ingestProjectService.getSubfolders(id).pipe(
            timeout(30000),
            map((items) => ({
              project,
              items: items ?? [],
              subfoldersError: null as string | null,
            })),
            catchError((err) =>
              of({
                project,
                items: [] as IngestSubfolder[],
                subfoldersError: messageFromIngestHttpError(
                  err,
                  'No se pudieron cargar las subcarpetas del expediente.',
                ),
              }),
            ),
          ),
        ),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ project, items, subfoldersError }) => {
          if (!this.applyInactiveProjectGuard(project)) {
            this.subfolders = [];
            return;
          }
          this.project = project;
          this.subfolders = normalizeIngestSubfolders(items);
          this.syncSubfolderOptions();
          if (subfoldersError) {
            this.subfoldersWarning.set(subfoldersError);
            openIngestAlertDialog(this.dialogService, {
              title: 'Error al cargar subcarpetas',
              message: subfoldersError,
              variant: 'error',
            });
          }
        },
        error: (err) => {
          const msg = messageFromIngestHttpError(
            err,
            'No se pudo cargar el expediente.',
          );
          const isTimeout =
            err?.name === 'TimeoutError' ||
            String(err?.message ?? '').toLowerCase().includes('timeout');
          this.error = isTimeout
            ? 'Tiempo de espera agotado al contactar el servidor. Revisa la red, el token y vuelve a intentar.'
            : msg;
          if (this.error) {
            openIngestAlertDialog(this.dialogService, {
              title: 'Error al cargar el expediente',
              message: this.error,
              variant: 'error',
            });
          }
        },
      });
  }

  private applyInactiveProjectGuard(project: IngestProject): boolean {
    if (isIngestProjectEligibleForUpload(project)) {
      this.error = null;
      return true;
    }
    this.project = null;
    this.error = ingestInactiveProjectMessage(project.name);
    return false;
  }

  onFilesChanged(files: UploadedFile[]): void {
    this.looseFiles = files.map((f) => f.file);
    this.rebuildPendingQueue();
  }

  onFolderInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const list = input.files;
    if (!list?.length) {
      input.value = '';
      return;
    }

    const entries: IngestFolderFileEntry[] = [];
    for (let i = 0; i < list.length; i++) {
      const file = list.item(i);
      if (!file) {
        continue;
      }
      entries.push({
        file,
        relativePath: normalizeIngestRelativePath(
          file.webkitRelativePath?.trim() || file.name,
        ),
      });
    }
    input.value = '';
    this.addFolderFileEntries(entries);
  }

  private addFolderFileEntries(entries: IngestFolderFileEntry[]): void {
    const rejected: IngestUploadFileValidationIssue[] = [];
    let added = 0;

    for (const { file, relativePath } of entries) {
      const issue = getIngestUploadFileValidationIssue(
        relativePath,
        file.size,
        this.maxFileBytes,
      );
      if (issue) {
        rejected.push(issue);
        continue;
      }
      this.folderEntries.push({
        file,
        relativePath,
        source: 'folder',
      });
      added++;
    }

    this.rebuildPendingQueue();

    if (rejected.length > 0) {
      const alert = formatIngestUploadFolderRejectionAlert(rejected, added);
      openIngestAlertDialog(this.dialogService, {
        title: alert.title,
        message: alert.message,
        variant: 'error',
      });
    }
  }

  onAddFolderClick(folderInput: HTMLInputElement): void {
    if (this.uploading()) {
      return;
    }

    if (isIngestDirectoryPickerAvailable()) {
      void this.pickFolderWithDirectoryApi();
      return;
    }

    folderInput.click();
  }

  private async pickFolderWithDirectoryApi(): Promise<void> {
    try {
      const entries = await pickIngestFolderFiles();
      if (!entries || entries.length === 0) {
        return;
      }
      this.addFolderFileEntries(entries);
    } catch {
      openIngestAlertDialog(this.dialogService, {
        title: 'No se pudo leer la carpeta',
        message: 'No fue posible acceder a los archivos de la carpeta seleccionada.',
        variant: 'error',
      });
    }
  }

  clearLooseFiles(): void {
    this.looseFiles = [];
    this.filePickerReset.update((n) => n + 1);
    this.rebuildPendingQueue();
  }

  clearFolderQueue(): void {
    this.folderEntries = [];
    this.rebuildPendingQueue();
  }

  clearAllQueued(): void {
    this.looseFiles = [];
    this.folderEntries = [];
    this.filePickerReset.update((n) => n + 1);
    this.rebuildPendingQueue();
  }

  private rebuildPendingQueue(): void {
    const loose: IngestPendingEntry[] = this.looseFiles.map((file) => ({
      file,
      relativePath: normalizeIngestRelativePath(file.name),
      source: 'loose',
    }));
    this.pendingEntries = [...loose, ...this.folderEntries];
    // Auto-detección del modo (control read-only): se actualiza al cargar archivos.
    this.uploadMode.set(
      isExpedienteSelection(this.pendingEntries.map((e) => e.file)) ? 'expediente' : 'documento',
    );
    this.uploadLines.set(
      this.pendingEntries.map((e, i) => ({
        id: `line-${i}-${e.relativePath}-${e.file.size}-${e.file.lastModified}`,
        fileName: e.relativePath,
        sizeBytes: e.file.size,
        status: 'pending',
        progress: 0,
      })),
    );
  }

  upload(): void {
    if (!this.project || !this.projectId) return;

    if (!this.companyName.trim()) {
      this.openUploadResultDialog({ kind: 'error', headline: 'El campo Empresa es obligatorio.' });
      return;
    }

    if (this.pendingEntries.length === 0) {
      this.openUploadResultDialog({
        kind: 'error',
        headline: 'Debes seleccionar al menos un archivo o una carpeta.',
      });
      return;
    }

    if (this.pendingEntries.length > 80) {
      this.openUploadResultDialog({
        kind: 'error',
        headline: 'Reduce el lote a 80 archivos o menos por ejecución.',
      });
      return;
    }

    const queueValidationError = this.findPendingUploadValidationError();
    if (queueValidationError) {
      this.openUploadResultDialog({
        kind: 'error',
        headline: queueValidationError,
      });
      return;
    }

    this.uploading.set(true);
    this.lastUploadSummary = null;

    const lines = this.uploadLines().map((l) => ({ ...l }));

    if (this.ingestEnv.previewMode) {
      this.runPreviewUploadSimulation(lines);
      return;
    }

    const projectName = this.project?.name ?? '';
    if (!projectName) {
      lines.forEach((l) => {
        l.status = 'error';
        l.errorMessage = 'No hay expediente seleccionado para la carga.';
      });
      this.uploadLines.set([...lines]);
      this.uploading.set(false);
      this.emitBatchResultToasts(lines);
      return;
    }

    const uploadMeta = this.snapshotUploadMetaForSend();

    // Modo EXPEDIENTE (1 .iso + 5 sidecars): carga resumable + fixity + commit.
    if (this.uploadMode() === 'expediente') {
      this.uploadExpediente(projectName, uploadMeta);
      return;
    }

    this.beginDocumentUpload(lines, uploadMeta);
  }

  private beginDocumentUpload(
    lines: UploadProgressLine[],
    uploadMeta: IngestUploadMetaSnapshot,
  ): void {
    if (!this.projectId) {
      this.uploading.set(false);
      return;
    }

    this.confirmIngestDuplicatesBeforeUpload$(uploadMeta)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (!result.proceed) {
            this.resetUploadUiAfterCancel(lines);
            return;
          }
          this.runIngestBatchRequest(
            lines,
            this.buildIngestBatchFormData(uploadMeta, result.confirmNewVersion),
          );
        },
        error: (err) => {
          this.uploading.set(false);
          this.openUploadResultDialog({
            kind: 'error',
            headline: messageFromIngestHttpError(
              err,
              'No se pudo validar duplicados antes de la carga.',
            ),
          });
        },
      });
  }

  /** Cancela el spinner de «Subir lote» y deja las líneas en pending. */
  private resetUploadUiAfterCancel(lines: UploadProgressLine[]): void {
    for (const line of lines) {
      line.status = 'pending';
      line.progress = 0;
      line.errorMessage = undefined;
    }
    this.uploadLines.set([...lines]);
    this.uploading.set(false);
  }

  private confirmIngestDuplicatesBeforeUpload$(
    uploadMeta: IngestUploadMetaSnapshot,
  ) {
    const projectId = this.projectId;
    if (!projectId) {
      return of({ proceed: true, confirmNewVersion: false });
    }

    const targets = resolveIngestDuplicateCheckTargets(this.pendingEntries);
    if (targets.length === 0) {
      return of({ proceed: true, confirmNewVersion: false });
    }

    return forkJoin(
      targets.map((target) =>
        this.ingestProjectService
          .checkDuplicate({
            projectId,
            filename: target.filename,
            subfolderPath: uploadMeta.subfolderPath || null,
          })
          .pipe(
            map((check) => ({ target, check })),
            catchError(() =>
              of({
                target,
                check: { exists: false, requires_confirmation: false },
              }),
            ),
          ),
      ),
    ).pipe(
      switchMap((results) => {
        const pending = results.filter((item) =>
          ingestDuplicateCheckRequiresConfirmation(item.check),
        );
        if (pending.length === 0) {
          return of({ proceed: true, confirmNewVersion: false });
        }

        return from(pending).pipe(
          concatMap(({ target, check }) =>
            openIngestConfirmDialog(this.dialogService, {
              title: 'Documento duplicado',
              message: formatIngestDuplicateConfirmMessage(check, target.filename),
              variant: 'warning',
              confirmLabel: 'Crear nueva versión',
              cancelLabel: 'Cancelar',
            }),
          ),
          // Al cancelar uno, no pedir el resto ni dejar el botón en loading.
          takeWhile((confirmed) => confirmed === true, true),
          toArray(),
          map((answers) => resolveIngestDuplicateConfirmDecision(answers, pending.length)),
        );
      }),
    );
  }

  private promptDuplicateConfirmationAndRetry(
    lines: UploadProgressLine[],
    uploadMeta: IngestUploadMetaSnapshot,
    check: IngestDuplicateCheckResponse,
  ): void {
    const fallback =
      resolveIngestDuplicateCheckTargets(this.pendingEntries)[0]?.filename ?? 'archivo';
    const filename = check.filename?.trim() || fallback;

    openIngestConfirmDialog(this.dialogService, {
      title: 'Confirmar nueva versión',
      message: formatIngestDuplicateConfirmMessage(check, filename),
      variant: 'warning',
      confirmLabel: 'Crear nueva versión',
      cancelLabel: 'Cancelar',
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (!confirmed) {
          this.resetUploadUiAfterCancel(lines);
          return;
        }
        this.runIngestBatchRequest(
          lines,
          this.buildIngestBatchFormData(uploadMeta, true),
        );
      });
  }

  private findPendingUploadValidationError(): string | null {
    for (const entry of this.pendingEntries) {
      const error = validateIngestUploadFile(
        entry.relativePath,
        entry.file.size,
        this.maxFileBytes,
      );
      if (error) {
        return error;
      }
    }
    return null;
  }

  /** Carga resumable del expediente (1 .iso + 5 sidecars) con verificación de fixity. */
  private uploadExpediente(projectName: string, uploadMeta: IngestUploadMetaSnapshot): void {
    const files = this.pendingEntries.map((e) => e.file);
    const iso = groupByRole(files).get('iso');
    const base = iso ? expedienteBaseFromIso(iso.name) : 'expediente';
    const line: UploadProgressLine = {
      id: `expediente-${base}`,
      fileName: `Expediente: ${base}`,
      sizeBytes: files.reduce((acc, f) => acc + f.size, 0),
      status: 'uploading',
      progress: 0,
    };
    this.uploadLines.set([line]);

    this.expedienteUpload
      .uploadExpediente$({
        files,
        projectName,
        companyName: uploadMeta.companyName,
        subfolderPath: uploadMeta.subfolderPath || null,
        description: uploadMeta.description || null,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.uploading.set(false);
          this.uploadLines.set([{ ...line }]);
        }),
      )
      .subscribe({
        next: (ev) => {
          if (ev.type === 'progress') {
            line.progress = Math.round((ev.overallSent * 100) / (ev.overallTotal || 1));
            this.uploadLines.set([{ ...line }]);
          } else {
            line.status = 'completed';
            line.progress = 100;
            this.uploadLines.set([{ ...line }]);
            const ingestId = ev.result.ingest_id;
            const fixity = ev.result.fixity_ok ? 'OK' : 'FALLÓ';
            this.lastUploadSummary = {
              status: ev.result.fixity_ok ? 'completed' : 'error',
              upload_id: String(ingestId ?? ''),
              message: `Expediente publicado (fixity ${fixity}).`,
              files: files.map((f) => f.name),
            } as IngestUploadResponse;
            this.openUploadResultDialog({
              kind: ev.result.fixity_ok ? 'success' : 'error',
              headline: `Expediente publicado (fixity ${fixity}).`,
              summary: this.lastUploadSummary,
            });
          }
        },
        error: (err) => {
          const msg = err?.message ?? 'Error en la carga del expediente.';
          line.status = 'error';
          line.errorMessage = msg;
          this.uploadLines.set([{ ...line }]);
          this.openUploadResultDialog({ kind: 'error', headline: msg });
        },
      });
  }

  /** Navega al recorrido del archivo del expediente recién publicado (ingest_id). */
  verRecorrido(): void {
    const id = this.lastUploadSummary?.upload_id;
    if (id) {
      this.router.navigate(['recorrido', id], { relativeTo: this.route.parent });
    }
  }

  private runIngestBatchRequest(lines: UploadProgressLine[], formData: FormData): void {
    const uploadMeta = this.snapshotUploadMetaForSend();

    lines.forEach((l) => {
      l.status = 'uploading';
      l.progress = 0;
      l.errorMessage = undefined;
    });
    this.uploadLines.set([...lines]);

    this.ingestProjectService.uploadIngest(formData).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          const total = event.total ?? 1;
          const pct = Math.round((event.loaded * 100) / total);
          for (const line of lines) {
            line.progress = pct;
          }
          this.uploadLines.set([...lines]);
        } else if (event.type === HttpEventType.Response) {
          const body = event.body;
          if (body) {
            for (const line of lines) {
              line.status = 'completed';
              line.progress = 100;
            }
            this.lastUploadSummary = body;
          } else {
            for (const line of lines) {
              line.status = 'error';
              line.errorMessage = 'Respuesta vacía del servidor.';
            }
          }
          this.uploadLines.set([...lines]);
        }
      },
      error: (err: unknown) => {
        const duplicateCheck = ingestDuplicateCheckFromUploadError(
          err,
          resolveIngestDuplicateCheckTargets(this.pendingEntries)[0]?.filename ?? 'archivo',
        );
        if (duplicateCheck) {
          this.promptDuplicateConfirmationAndRetry(lines, uploadMeta, duplicateCheck);
          return;
        }
        for (const line of lines) {
          line.status = 'error';
          line.errorMessage = messageFromIngestUploadError(err);
        }
        this.uploadLines.set([...lines]);
        this.uploading.set(false);
        this.emitBatchResultToasts(lines);
      },
      complete: () => {
        this.uploading.set(false);
        this.emitBatchResultToasts(lines);
      },
    });
  }

  /**
   * Mismo contrato que el front React (`frontend/.../vault/page.tsx`):
   * un solo `POST /api/ingest/upload` con varias claves `files` y, si hay carpeta,
   * `file_path_0`, `file_path_1`, ... (ruta relativa webkit). Luego `is_folder` y metadatos.
   * No se usa `relative_path` por request (eso era 1 request por archivo).
   */
  private buildIngestBatchFormData(
    uploadMeta: IngestUploadMetaSnapshot,
    confirmNewVersion = false,
  ): FormData {
    const formData = new FormData();
    this.pendingEntries.forEach((entry, index) => {
      formData.append('files', entry.file);
      if (entry.source === 'folder') {
        formData.append(
          `file_path_${index}`,
          normalizeIngestRelativePath(entry.relativePath),
        );
      }
    });
    const hasFolderStructure = this.pendingEntries.some((e) => e.source === 'folder');
    appendIngestUploadMetaFields(
      formData,
      uploadMeta,
      this.project?.name ?? '',
      hasFolderStructure,
      confirmNewVersion,
    );
    return formData;
  }

  private runPreviewUploadSimulation(lines: UploadProgressLine[]): void {
    lines.forEach((line, i) => {
      line.status = 'uploading';
      window.setTimeout(() => {
        line.progress = 100;
        line.status = 'completed';
        this.uploadLines.set([...lines]);
        if (i === lines.length - 1) {
          this.uploading.set(false);
          const mock: IngestUploadResponse = {
            status: 'preview',
            upload_id: `preview-${Date.now()}`,
            message: 'Vista prevía: no se envió nada al servidor.',
            files: this.pendingEntries.map((e) => e.relativePath),
          };
          this.lastUploadSummary = mock;
          this.finalizeSuccessfulUpload();
          this.openUploadResultDialog({
            kind: 'success',
            headline: 'Vista prevía',
            intro: 'Simulación local: no se envió nada al servidor.',
            summary: mock,
          });
        }
      }, 180 + i * 120);
    });
    this.uploadLines.set([...lines]);
  }

  private emitBatchResultToasts(lines: UploadProgressLine[]): void {
    const n = lines.length;
    if (n === 0) {
      return;
    }
    const failed = lines.filter((l) => l.status === 'error');
    const ok = lines.filter((l) => l.status === 'completed');
    if (shouldResetIngestUploadMetaAfterBatch(lines)) {
      this.finalizeSuccessfulUpload();
      this.openUploadResultDialog({
        kind: 'success',
        headline: 'Carga completada',
        intro: `Se subieron ${ok.length} archivo(s) correctamente.`,
        summary: this.lastUploadSummary,
      });
      return;
    }
    if (ok.length === 0) {
      this.openUploadResultDialog({
        kind: 'error',
        headline:
          failed[0]?.errorMessage || 'No se pudo completar la carga de los archivos.',
      });
      return;
    }
    this.openUploadResultDialog({
      kind: 'error',
      headline: `${failed.length} de ${n} archivos fallaron. ${failed[0]?.errorMessage || ''}`.trim(),
    });
  }

  goBackToDetail(): void {
    if (!this.projectId) return;
    if (!this.canLeaveUploadScreen()) {
      return;
    }
    void this.router.navigate(['projects', this.projectId], {
      relativeTo: this.route.parent,
    });
  }

  onHeaderAction(actionId: string): void {
    if (actionId === 'back') {
      this.goBackToDetail();
    } else if (actionId === 'upload') {
      this.upload();
    }
  }

  /**
   * Guard de ruta / Volver: no salir mientras corre la ingesta.
   * Usado por `canDeactivate` en `ingest.routes`.
   */
  canDeactivateUpload(): boolean {
    return this.canLeaveUploadScreen();
  }

  /** Cierre de pestaña/navegador: aviso nativo si hay carga en curso. */
  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.uploading()) {
      return;
    }
    event.preventDefault();
    event.returnValue = true;
  }

  /**
   * @returns true si se puede salir; false si hay carga (muestra aviso).
   */
  private canLeaveUploadScreen(): boolean {
    if (canLeaveIngestUploadScreen(this.uploading())) {
      return true;
    }
    openIngestAlertDialog(this.dialogService, {
      title: INGEST_UPLOAD_IN_PROGRESS_LEAVE_TITLE,
      message: INGEST_UPLOAD_IN_PROGRESS_LEAVE_MESSAGE,
      variant: 'warning',
    });
    return false;
  }

}
